/**
 * Hide the header while scrolling down, bring it back on the first upward scroll.
 *
 * Reads scroll position inside requestAnimationFrame so the listener itself does no
 * layout work; the passive listener never blocks scrolling.
 *
 * Two guards keep it from feeling twitchy:
 * - nothing hides until the page has scrolled past the header's own height, so a
 *   small nudge at the top of the page can't snatch the nav away;
 * - direction changes must exceed a few pixels, which absorbs the jitter of
 *   trackpad momentum and rubber-band scrolling.
 */
(() => {
  const header = document.querySelector('.header');
  const wrapper = header?.parentElement;
  if (!header || !wrapper) return;

  const DIRECTION_THRESHOLD = 6;
  const TOP_THRESHOLD = 2;

  let lastY = window.scrollY;
  let ticking = false;

  function update() {
    ticking = false;
    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;

    if (y <= TOP_THRESHOLD) {
      // Back at the top: reveal, and drop the solid background so a transparent
      // header returns to floating over the artwork behind it.
      wrapper.classList.remove('header--hidden');
      header.classList.remove('header--scrolled');
      lastY = y;
      return;
    }

    header.classList.add('header--scrolled');

    if (Math.abs(delta) < DIRECTION_THRESHOLD) return;

    if (delta > 0 && y > wrapper.offsetHeight) {
      wrapper.classList.add('header--hidden');
    } else if (delta < 0) {
      wrapper.classList.remove('header--hidden');
    }

    lastY = y;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
})();

/**
 * <menu-panel> — the mobile navigation, dropping out of the header's bottom edge.
 *
 * Deliberately not a modal <dialog> like a drawer: a modal takes the top layer and
 * makes the page behind it inert, which would put the header — and the control that
 * opened this — out of reach. Here the header stays live, so one control opens and
 * closes, and the panel only has to manage its own open state.
 */
(() => {
  // Every panel hanging off the header's bottom edge. Two of them open at once would
  // sit on top of each other, so opening one closes the rest.
  const PANELS = ['menu-panel', 'search-panel', 'cart-drawer'];

  class HeaderPanel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-panel-close]')) this.close();
      });
      document.addEventListener('keydown', this.#onKeydown);

      // A panel whose trigger only exists on one side of the breakpoint would be left
      // open with nothing to close it.
      this.breakpoint = window.matchMedia('(min-width: 62.0625rem)');
      this.breakpoint.addEventListener('change', () => this.close());
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.#onKeydown);
    }

    open() {
      PANELS.filter((id) => id !== this.id).forEach((id) =>
        document.getElementById(id)?.close?.()
      );

      this.setAttribute('data-open', '');
      // scrollbarGutter holds the page width while the bar is gone, so nothing behind
      // the panel shifts sideways as it opens.
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.scrollbarGutter = 'stable';
      this.#reflect(true);
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('data-open');
      document.documentElement.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
      this.#reflect(false);
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    /* The trigger is also the close button, so it carries the state: the icon reads
       it to draw itself, and the label has to say which of the two it currently is. */
    #reflect(open) {
      document.querySelectorAll(`[data-panel-open="${this.id}"]`).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(open));
        const label = open ? trigger.dataset.labelClose : trigger.dataset.labelOpen;
        if (label) trigger.setAttribute('aria-label', label);
      });
    }

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
  }

  if (!customElements.get('menu-panel')) {
    customElements.define('menu-panel', HeaderPanel);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-panel-open]');
    if (!trigger) return;

    const panel = document.getElementById(trigger.dataset.panelOpen);
    if (!(panel instanceof HeaderPanel)) return;

    event.preventDefault();
    panel.isOpen ? panel.close() : panel.open();
  });
})();

/**
 * Steps the chat launcher aside when it is in the way.
 *
 * The open panels are handled in CSS; this is the other half — the footer, which has
 * to be watched rather than matched. Shopify's widget is fixed to the bottom corner,
 * exactly where the footer puts its contact links, so it sits on top of the answers
 * a shopper came looking for.
 */
(() => {
  const footer = document.querySelector('.footer');
  if (!footer || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    ([entry]) => document.documentElement.classList.toggle('chat--tucked', entry.isIntersecting),
    // A sliver of footer showing is not yet reason to move: it steps aside once a
    // fair part of it is on screen.
    { threshold: 0.15 }
  );

  observer.observe(footer);
})();
